const T = require('prop-types');

const DashboardDependencies = require('./dashboard-dependencies');

const internals = {};

const ACTIONS = [
    {
        id: 'dashboard',
        label: 'Admin Dashboard',
        description: 'Review community health and school-level reporting.',
        path: '/app/admin/dashboard',
        permissions: ['canViewDashboard']
    },
    {
        id: 'manage-communities',
        label: 'Manage Communities',
        description: 'Review and maintain community configuration.',
        path: '/app/manage-communities',
        permissions: [
            'canViewDashboard',
            'canBatchNotify',
            'canPreapprove',
            'canCreateGroups',
            'canEditGroups'
        ],
        permissionMode: 'any'
    },
    {
        id: 'batch-notify',
        label: 'Batch Notify Users',
        description: 'Send targeted operational messages to community members.',
        path: '/app/admin/batch-notify-users',
        permissions: ['canBatchNotify']
    },
    {
        id: 'preapprove-users',
        label: 'Preapprove Users',
        description: 'Upload and manage preapproved email lists.',
        path: '/app/admin/preapprove-users',
        permissions: ['canPreapprove']
    },
    {
        id: 'welcome-page',
        label: 'Manage Welcome Page',
        description: 'Edit the school-wide and role-specific welcome page messages.',
        path: '/app/admin/welcome-page',
        permissions: ['canManageWelcomePage']
    },
    {
        id: 'survey-management',
        label: 'Survey Management',
        description: 'Create and manage community surveys.',
        path: '/app/surveys/management',
        permissions: ['canCreateSurveys']
    }
];

exports.requiredPermissions = [
    'canViewDashboard',
    'canBatchNotify',
    'canPreapprove',
    'canCreateGroups',
    'canEditGroups',
    'canCreateSurveys',
    'canSeeSurveysReports',
    'canManageWelcomePage'
];

exports.dependencyPropTypes = {
    currentUser: T.object.isRequired,
    rolePermissions: T.object.isRequired,
    schools: T.array
};

exports.getTeamConsoleModel = ({ currentUser = {}, rolePermissions = {}, schools = [] } = {}) => {

    const canViewAllSchools = DashboardDependencies.canViewAllDashboardSchools(currentUser);
    const visibleSchools = DashboardDependencies.getDashboardSchoolOptions({
        currentUser,
        schools
    });
    const currentSchool = visibleSchools.find((school) => school.id === currentUser.schoolId)
        || schools.find((school) => school.id === currentUser.schoolId)
        || null;

    return {
        currentUser: {
            id: currentUser.id,
            email: currentUser.email,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            schoolId: currentUser.schoolId
        },
        access: {
            canViewAllSchools,
            schoolScopeLabel: canViewAllSchools ? 'All schools' : currentSchool?.name || 'Current school',
            visibleSchoolCount: visibleSchools.length
        },
        currentSchool,
        capabilities: internals.getCapabilities(rolePermissions),
        actions: exports.getVisibleActions({ rolePermissions })
    };
};

exports.getVisibleActions = ({ rolePermissions = {} } = {}) => ACTIONS.filter((action) => (
    internals.hasActionPermission({
        action,
        rolePermissions
    })
));

internals.getCapabilities = (rolePermissions = {}) => ({
    dashboard: !!rolePermissions.canViewDashboard,
    batchNotify: !!rolePermissions.canBatchNotify,
    preapprove: !!rolePermissions.canPreapprove,
    groups: !!(rolePermissions.canCreateGroups || rolePermissions.canEditGroups),
    surveys: !!(rolePermissions.canCreateSurveys || rolePermissions.canSeeSurveysReports),
    welcomePage: !!rolePermissions.canManageWelcomePage
});

internals.hasActionPermission = ({ action, rolePermissions }) => {

    const permissions = action.permissions || [];

    if (action.permissionMode === 'any') {
        return permissions.some((permission) => !!rolePermissions[permission]);
    }

    return permissions.every((permission) => !!rolePermissions[permission]);
};

if (process.env.NODE_ENV === 'test') {
    exports._internals = internals;
}
